<script lang="ts" setup>
import OmniDialog from "@/components/omni/OmniDialog.vue";
import OmniTipTap from "@/components/omni/OmniTipTap.vue";
//@ts-ignore
import EmailsField from "@/components/inputs/EmailsField.vue";
import { computed, nextTick, onMounted, ref } from "vue";
import type { VForm } from "vuetify/components";
import type {
  CreateNewUpdateRequest,
  PreviewUpdateRequest,
  PreviewUpdateResponse,
  TripUpdate,
  TripUpdatePreview,
} from "@/api/trips/dto/update";
import { useSnackbarStore } from "@/store/snackbar.store";
import { API } from "@/api/api";
import GoogleMapsMixin from "@/mixins/google-maps.mixin";
import OmniGoogleMap from "@/components/omni/OmniGoogleMap.vue";
import FileUploadingGrid from "@/views/trips/components/FileUploadingGrid.vue";
import AlertMessage from "@/components/messages/AlertMessage.vue";
import { useI18n } from "vue-i18n";
import ViewUpdate from "@/views/trips/components/ViewUpdate.vue";
import { useValidation } from "@/composables/useValidation";
import type { Recipient } from "@/types/recipients";
import { useBaseView } from "@/composables";
import OrderAndNumberRefs from "@/views/trips/components/OrderAndNumberRefs.vue";
import type { BrokerUpdateFormProps } from "@/types/props";
import { setInlineEmailTableStyles } from "@/utils/inlineEmailTableStyles";

const api = new API();
const { t } = useI18n();

const { getApiKey } = GoogleMapsMixin.methods;

const { showSnackbarError } = useSnackbarStore();
const { requiredValidator, arrayRequiredValidator, notFoundedRecipientsValidator } = useValidation();
const { getPreviewPath, scrollToError } = useBaseView();

const props = defineProps<BrokerUpdateFormProps>();

const emits = defineEmits<{
  (e: "success", update: TripUpdate, travelOrderId: number): void;
  (e: "close"): void;
}>();

const formRef = ref<VForm | null>(null);
const recipientsRef = ref<any>(null);
const loading = ref<boolean>(false);
const mapCenter = ref();
const fields = ref<Omit<PreviewUpdateResponse, "messageIdError">>({
  subject: "",
  message: "",
  messageId: "",
  recipients: [],
  files: [],
  failedRecipients: [],
});
const messageIdErrorRef = ref<string>();
const lastPreviewedMessageId = ref<string>("");
const selectedFiles = ref<string[]>([]);
const initialRecipients = ref<string[] | null>(null);
const initialFailedRecipients = ref<string[] | null>(null);
const isErrorPreparedUpdate = ref<boolean>(false);
const showRecipientsMessage = ref<boolean>();
const isUpdatingRecipients = ref<boolean>();
const recipients = ref<Recipient[]>([]);
const isLoaded = ref<boolean>(false);
const isPreview = ref<boolean>(false);
const showFilesValidationDialog = ref<boolean>(false);

const title = computed(() => {
  if (isPreview.value) {
    return `${t("updates.broker-update-preview-for")} Travel order#${props.travelOrderId}`;
  }

  return `${t("updates.create-update-for")} Travel order#${props.travelOrderId}`;
});

const isMapVisible = computed(() => {
  return false;
});

const updatePreview = computed<TripUpdatePreview>(() => {
  return {
    message: fields.value.message,
    subject: fields.value.subject,
    emailRecipients: fields.value.recipients,
    files: fields.value.files.filter((file) => selectedFiles.value.includes(file.id)),
  };
});

const primaryButtonLabel = computed(() => {
  if (isPreview.value) {
    return t("general.create");
  }

  return t("general.preview");
});

const secondaryButtonLabel = computed(() => {
  if (isPreview.value) {
    return t("updates.edit-update");
  }

  return "";
});

const isPrimaryDisabled = computed(() => isErrorPreparedUpdate.value);

const isPrimaryLoading = computed(() => !isLoaded.value || Boolean(isUpdatingRecipients.value) || loading.value);

const isSecondaryLoading = computed(() => loading.value);

async function create() {
  if (!props.routeId || !props.travelOrderId) {
    return;
  }

  const data: CreateNewUpdateRequest = {
    checkCallId: props.checkCallId,
    travelOrderId: props.travelOrderId,
    subject: fields.value.subject,
    messageId: fields.value.messageId,
    message: setInlineEmailTableStyles(fields.value.message),
    recipients: fields.value.recipients,
    files: selectedFiles.value,
  };

  loading.value = true;
  try {
    const response = await api.events.createUpdate(props.routeId, data);
    emits("close");

    if (response.data) {
      emits("success", response.data, props.travelOrderId);
    }
  } catch (e) {
    showSnackbarError("Could not create new update");
  } finally {
    loading.value = false;
  }
}

async function showPreview(confirm = false) {
  showFilesValidationDialog.value = false;

  const result = await formRef.value?.validate();

  if (!result?.valid) {
    scrollToError();

    return;
  }

  if (fields.value.files.length && !selectedFiles.value.length && !confirm) {
    showFilesValidationDialog.value = true;

    return;
  }

  isPreview.value = true;
}

function editUpdate() {
  isPreview.value = false;
}

async function onPrimaryClick() {
  if (isPreview.value) {
    await create();

    return;
  }
  const result = await formRef.value?.validate();

  if (!result?.valid) {
    scrollToError();
    return;
  }

  await showPreview();
}

function checkIsRecipientsChanged() {
  const initial = [...(initialRecipients.value || []), ...(initialFailedRecipients.value || [])].slice().sort();
  const current = (recipients.value.flatMap((recipient) => recipient.value) || []).slice().sort();

  if (initial.length !== current.length) {
    return true;
  }

  return !initial.every((recipient, index) => recipient === current[index]);
}

function checkExistedRecipient(email: string) {
  return recipients.value.some((recipient) => recipient.value === email);
}

function setRecipients() {
  fields.value.recipients.forEach((email) => {
    if (checkExistedRecipient(email)) {
      return;
    }

    const isNew = isLoaded.value && !initialRecipients.value?.includes(email);

    const recipient: Recipient = {
      value: email,
      class: isNew ? "border-success bg-green-lighten-5" : "",
    };

    recipients.value.push(recipient);
  });

  fields.value.failedRecipients.forEach((failedRecipient) => {
    if (checkExistedRecipient(failedRecipient)) {
      return;
    }

    const recipient: Recipient = {
      value: failedRecipient,
      class: "border-red-lighten-5 bg-red-lighten-5",
      notFounded: true,
    };

    recipients.value.push(recipient);
  });
}

async function validateRecipientsFields() {
  await nextTick();
  recipientsRef?.value.$refs?.emailsField?.validate();
}

async function onMessageIdBlur() {
  if (fields.value.messageId && fields.value.messageId !== lastPreviewedMessageId.value) {
    await getPreviewInfo();
  }
}

async function getPreviewInfo() {
  if (isLoaded.value) {
    isUpdatingRecipients.value = true;
  }

  try {
    const request: PreviewUpdateRequest = {
      checkCallId: props.checkCallId,
      travelOrderId: props.travelOrderId,
      messageId: fields.value.messageId,
    };

    lastPreviewedMessageId.value = request.messageId;

    const response = await api.events.previewUpdate(props.routeId, request);

    if (response.success && response.data) {
      const { messageIdError, ...rest } = response.data;

      fields.value = {
        ...rest,
        files: rest.files.map((file) => {
          return {
            ...file,
            previewPath: getPreviewPath(file),
          };
        }),
      };

      setRecipients();

      if (!initialRecipients.value) {
        initialRecipients.value = fields.value.recipients;
      }

      if (!initialFailedRecipients.value) {
        initialFailedRecipients.value = fields.value.failedRecipients;
      }

      messageIdErrorRef.value = messageIdError;
      showRecipientsMessage.value = checkIsRecipientsChanged();
    } else {
      isErrorPreparedUpdate.value = true;
    }
  } catch (error) {
    isErrorPreparedUpdate.value = true;
    showSnackbarError("Could not load update preview");
  } finally {
    isLoaded.value = true;
    isUpdatingRecipients.value = false;
    await validateRecipientsFields();
  }
}

function setFile(fileId: string) {
  const index = selectedFiles.value.indexOf(fileId);

  if (index !== -1) {
    selectedFiles.value.splice(index, 1);
  } else {
    selectedFiles.value.push(fileId);
  }
}

function editRecipients(newRecipients: Recipient[]) {
  recipients.value = newRecipients.map((recipient) => {
    if (initialFailedRecipients.value?.includes(recipient.value)) {
      return {
        ...recipient,
        class: "border-red-lighten-5 bg-red-lighten-5",
        notFounded: true,
      };
    }

    if (recipient.notFounded) {
      return {
        ...recipient,
        notFounded: false,
        class: "",
      };
    }

    return recipient;
  });

  fields.value.recipients = recipients.value.flatMap((recipient) => recipient.value);

  validateRecipientsFields();
}

async function onMessageIdUpdate(value: string) {
  fields.value.messageId = value.trim();
}

onMounted(async () => {
  await getPreviewInfo();
});
</script>

<template>
  <div class="broker-update-form d-flex flex-column flex-grow-1">
    <div class="broker-update-form__header pa-6 pb-0 d-flex justify-space-between align-center">
      <div class="font-size-20 font-weight-500 text-grey-darken-4 line-height-24px text-wrap">
        {{ title }}
      </div>
    </div>

    <div class="broker-update-form__body pa-6 flex-grow-1 overflow-y-auto">
      <order-and-number-refs v-if="Boolean(props.orders?.length)" :orders="props.orders" class="mb-5" />

      <transition mode="out-in">
        <v-form v-if="!isPreview" ref="formRef">
          <emails-field
            ref="recipientsRef"
            v-model="recipients"
            :disabled="isUpdatingRecipients || !isLoaded"
            :rules="[arrayRequiredValidator(recipients), notFoundedRecipientsValidator(recipients)]"
            data-qa="email-field"
            @update:model-value="editRecipients"
          />

          <alert-message
            v-if="showRecipientsMessage && !isUpdatingRecipients"
            bg-color="green-lighten-5"
            border-color="success"
            class="mt-3"
            icon="mdi-check"
            icon-color="success"
          >
            The recipient list has been updated
          </alert-message>

          <alert-message
            v-if="isUpdatingRecipients"
            bg-color="green-lighten-5"
            border-color="success"
            class="mt-3"
            icon="mdi-loading"
            icon-class="recipients-spinner"
            icon-color="success"
          >
            Updating recipients…
          </alert-message>

          <v-text-field
            v-model="fields.messageId"
            :disabled="isUpdatingRecipients || !isLoaded"
            :error="Boolean(messageIdErrorRef)"
            :error-messages="messageIdErrorRef"
            autocomplete="off"
            class="mt-5"
            density="compact"
            label="Message ID"
            variant="outlined"
            @blur="onMessageIdBlur"
            @update:model-value="onMessageIdUpdate"
          />

          <div v-if="messageIdErrorRef" class="mt-4"></div>

          <v-text-field
            v-model="fields.subject"
            :disabled="(Boolean(fields.messageId) && !Boolean(messageIdErrorRef)) || !isLoaded"
            :rules="[requiredValidator]"
            autocomplete="off"
            class="required"
            data-qa="subject-field"
            density="compact"
            label="Subject"
            variant="outlined"
          />

          <omni-tip-tap v-model="fields.message" :disable-toolbar="!isLoaded" :disabled="!isLoaded" class="mt-2" />

          <div v-if="fields.files.length" class="mb-6 mt-2">
            <div class="font-size-16 text-grey-darken-3 font-weight-500 mb-3">Files</div>

            <span class="text-grey-darken-2">{{ t("updates.select-files") }}</span>

            <file-uploading-grid v-model="fields.files" class="mt-4" disabled>
              <template #prefix-name="{ file }">
                <v-checkbox
                  :model-value="selectedFiles.includes(file.id)"
                  class="mr-3"
                  color="primary"
                  density="compact"
                  hide-details
                  @change="setFile(file.id)"
                ></v-checkbox>
              </template>
            </file-uploading-grid>
          </div>

          <omni-google-map
            v-if="isMapVisible"
            :api-key="getApiKey()"
            :center="mapCenter"
            :fullscreen-control="false"
            :street-view-control="false"
            :zoom="4"
            hide-legend
            style="width: 100%; height: 450px"
          >
          </omni-google-map>
        </v-form>

        <view-update v-else :check-call-type="props.checkCallType" :update="updatePreview" />
      </transition>
    </div>

    <div class="broker-update-form__footer pa-6 pt-0 d-flex align-center">
      <v-spacer></v-spacer>

      <v-btn
        v-if="secondaryButtonLabel"
        :loading="isSecondaryLoading"
        class="text-uppercase font-weight-600 font-size-14 px-4 mr-3"
        color="primary"
        variant="outlined"
        @click="editUpdate"
      >
        {{ secondaryButtonLabel }}
      </v-btn>

      <v-btn
        :disabled="isPrimaryDisabled"
        :loading="isPrimaryLoading"
        class="text-uppercase font-weight-600 font-size-14 px-4"
        color="primary"
        variant="flat"
        @click="onPrimaryClick"
      >
        {{ primaryButtonLabel }}
      </v-btn>
    </div>

    <omni-dialog
      v-model="showFilesValidationDialog"
      :primary-action-label="t('general.attach-file')"
      :secondary-action-label="t('general.continue-without-attaching')"
      :title="t('updates.files-validation-error')"
      @primary-action:click="showFilesValidationDialog = false"
      @secondary-action:click="showPreview(true)"
    >
      <span class="text-grey-darken-1">{{ t("updates.confirm-create-without-files") }}</span>
    </omni-dialog>
  </div>
</template>

<style lang="scss">
.broker-update-form {
  min-height: 0;

  &__body {
    min-height: 0;
  }
}

.recipients-spinner {
  animation: 1s infinite linear spin;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
